import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  message,
  Alert,
  Typography,
  Input,
  Select,
  Spin,
  Tree,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { usePricingData } from "./hooks/usePricingData";
import type { PricingRule, PricingComponent } from "./types";
import { AddRuleModal } from "./components/AddRuleModal";
import { ComponentsDrawer } from "./components/ComponentsDrawer";
import { supabase } from "../../../lib/supabase";

const { Title, Text } = Typography;
const { Search } = Input;

// Reference data constants based on database structure [[memory:2731766]]
const REFERENCE_DATA = {
  transportTypes: [
    { id: 1, name: "Airfreight", abbreviation: "AIR" },
    { id: 2, name: "Seafreight", abbreviation: "SEA" },
  ],
  bookingTypes: [
    { id: 1, name: "LCL" },
    { id: 2, name: "FCL" },
  ],
  containerSizes: [
    { id: 1, name: "20ft" },
    { id: 2, name: "40ft" },
  ],
  incoterms: [
    { id: 1, name: "EXW" },
    { id: 2, name: "FCA" },
    { id: 3, name: "FOB" },
  ],
  originPorts: [
    { id: 1, name: "Shenzhen", country: "China" },
    { id: 2, name: "Shanghai", country: "China" },
    { id: 3, name: "Ningbo", country: "China" },
    { id: 6, name: "Hong Kong", country: "Hong Kong" },
  ],
  destinationPorts: [
    { id: 1, name: "Rotterdam", country: "Netherlands" },
    { id: 2, name: "Amsterdam", country: "Netherlands" },
  ],
};

interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
  rule?: PricingRule;
  level: string;
  totalCost: number;
  isLeaf?: boolean;
}

interface RuleFormData {
  name: string;
  description?: string;
  valid_from: string;
  valid_to?: string;
  transport_type?: number;
  origin_port?: number;
  destination_port?: number;
  container_size?: number;
  incoterm?: number;
  shipment_type?: number;
  priority: number;
  is_active: boolean;
}

export function TreePricingTab2() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransportType, setSelectedTransportType] = useState<
    number | undefined
  >();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isComponentsDrawerVisible, setIsComponentsDrawerVisible] =
    useState(false);
  const [selectedRule, setSelectedRule] = useState<PricingRule | null>(null);
  const [selectedTreeNode, setSelectedTreeNode] = useState<TreeNode | null>(
    null
  );
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [components, setComponents] = useState<PricingComponent[]>([]);

  const { loading, rules, loadPricingRules, deleteRule } = usePricingData();

  // Check for duplicate rules
  const checkForDuplicates = (
    newRule: Partial<RuleFormData>
  ): string | null => {
    const existing = rules.find((rule) => {
      return (
        rule.transport_type === newRule.transport_type &&
        rule.shipment_type === newRule.shipment_type &&
        rule.incoterm === newRule.incoterm &&
        rule.origin_port === newRule.origin_port &&
        rule.destination_port === newRule.destination_port
      );
    });

    if (existing) {
      const parts: string[] = [];

      if (newRule.transport_type) {
        const transportType = REFERENCE_DATA.transportTypes.find(
          (t) => t.id === newRule.transport_type
        );
        if (transportType) parts.push(transportType.name);
      }

      if (newRule.shipment_type && newRule.transport_type === 2) {
        const shipmentType = REFERENCE_DATA.bookingTypes.find(
          (t) => t.id === newRule.shipment_type
        );
        if (shipmentType) parts.push(shipmentType.name);
      }

      if (newRule.incoterm) {
        const incoterm = REFERENCE_DATA.incoterms.find(
          (i) => i.id === newRule.incoterm
        );
        if (incoterm) parts.push(incoterm.name);
      }

      const description =
        parts.length > 0 ? parts.join(" - ") : "this combination";
      return `A rule for ${description} already exists (Rule: "${existing.name}"). Please modify the existing rule or choose different criteria.`;
    }

    return null;
  };

  // Build simple tree structure
  const buildTreeStructure = (): TreeNode[] => {
    const tree: TreeNode[] = [];

    // Group rules by transport type
    const transportGroups = new Map<number | null, PricingRule[]>();
    rules.forEach((rule) => {
      const key = rule.transport_type;
      if (!transportGroups.has(key)) {
        transportGroups.set(key, []);
      }
      transportGroups.get(key)!.push(rule);
    });

    // Create transport type nodes
    for (const [transportTypeId, transportRules] of transportGroups) {
      const transportType = transportTypeId
        ? REFERENCE_DATA.transportTypes.find((t) => t.id === transportTypeId)
        : null;

      const transportNode: TreeNode = {
        key: `transport-${transportTypeId || "global"}`,
        title: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              {transportType ? `✈️ ${transportType.name}` : "🌍 Global"}
            </span>
            <div>
              <Button
                size="small"
                icon={<SettingOutlined />}
                style={{ marginLeft: 8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  const rule = transportRules.find(
                    (r) =>
                      !r.shipment_type &&
                      !r.incoterm &&
                      !r.origin_port &&
                      !r.destination_port
                  );
                  if (rule) {
                    setSelectedRule(rule);
                    setIsComponentsDrawerVisible(true);
                  }
                }}
              >
                Components
              </Button>
            </div>
          </div>
        ),
        children: [],
        level: "transport",
        totalCost: transportRules.reduce(
          (sum, rule) => sum + rule.total_cost,
          0
        ),
        rule: transportRules.find(
          (r) =>
            !r.shipment_type &&
            !r.incoterm &&
            !r.origin_port &&
            !r.destination_port
        ),
      };

      // Add individual rules as children
      transportRules.forEach((rule) => {
        const ruleNode: TreeNode = {
          key: `rule-${rule.id}`,
          title: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <Text strong>{rule.name}</Text>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  €{rule.total_cost.toFixed(2)} • {rule.component_count}{" "}
                  components
                </div>
              </div>
              <div>
                <Button
                  size="small"
                  icon={<SettingOutlined />}
                  style={{ marginRight: 4 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRule(rule);
                    setIsComponentsDrawerVisible(true);
                  }}
                >
                  Components
                </Button>
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteRule(rule.id);
                  }}
                />
              </div>
            </div>
          ),
          level: "rule",
          totalCost: rule.total_cost,
          rule: rule,
          isLeaf: true,
        };

        transportNode.children!.push(ruleNode);
      });

      tree.push(transportNode);
    }

    return tree;
  };

  const loadComponents = async () => {
    try {
      const { data, error } = await supabase
        .from("pricing_components")
        .select("*");
      if (error) throw error;
      setComponents(data || []);
    } catch (error) {
      console.error("Error loading components:", error);
    }
  };

  const handleAddRule = async (ruleData: RuleFormData) => {
    const duplicateError = checkForDuplicates(ruleData);
    if (duplicateError) {
      message.error(duplicateError);
      return;
    }

    try {
      // Clear fields that don't apply to airfreight
      if (ruleData.transport_type === 1) {
        // Airfreight
        ruleData.shipment_type = undefined;
        ruleData.container_size = undefined;
      }

      const { error } = await supabase.from("pricing_rules").insert({
        name: ruleData.name,
        description: ruleData.description,
        transport_type: ruleData.transport_type || null,
        origin_port: ruleData.origin_port || null,
        destination_port: ruleData.destination_port || null,
        shipment_type: ruleData.shipment_type || null,
        incoterm: ruleData.incoterm || null,
        is_active: ruleData.is_active,
        valid_from: ruleData.valid_from,
        valid_to: ruleData.valid_to,
        priority: ruleData.priority,
      });

      if (error) throw error;

      message.success("Rule added successfully");
      setIsAddModalVisible(false);
      loadPricingRules();
    } catch (error) {
      console.error("Error adding rule:", error);
      message.error("Failed to add rule");
    }
  };

  const handleTreeSelect = (
    selectedKeys: React.Key[],
    info: { node: TreeNode }
  ) => {
    if (selectedKeys.length > 0) {
      const node = info.node as TreeNode;
      setSelectedTreeNode(node);
      if (node.rule) {
        setSelectedRule(node.rule);
      }
    }
  };

  useEffect(() => {
    loadPricingRules();
    loadComponents();
  }, []);

  useEffect(() => {
    if (rules.length > 0) {
      const tree = buildTreeStructure();
      setTreeData(tree);
      // Auto-expand first level
      setExpandedKeys(tree.map((node) => node.key));
    }
  }, [rules]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          🚀 Pricing Rules V2 - Tree View
        </Title>
        <Text type="secondary">
          Hierarchical pricing with inheritance: Transport → Rules with
          components
        </Text>
      </div>

      {/* Filters and Actions */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Search
            placeholder="Search rules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder="Filter by transport type"
            value={selectedTransportType}
            onChange={setSelectedTransportType}
            allowClear
            style={{ width: "100%" }}
          >
            {REFERENCE_DATA.transportTypes.map((type) => (
              <Select.Option key={type.id} value={type.id}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={12} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddModalVisible(true)}
            style={{ marginRight: 8 }}
          >
            Add Rule
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadPricingRules();
              loadComponents();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Split Layout */}
      <Row gutter={16} style={{ flex: 1, height: "calc(100vh - 200px)" }}>
        {/* Left: Tree View */}
        <Col span={12}>
          <Card
            title="Pricing Tree"
            size="small"
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
            bodyStyle={{ flex: 1, overflow: "auto" }}
          >
            {loading ? (
              <div style={{ textAlign: "center", padding: "50px 0" }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading pricing tree...</div>
              </div>
            ) : (
              <Tree
                treeData={treeData}
                expandedKeys={expandedKeys}
                onExpand={(expandedKeys) =>
                  setExpandedKeys(expandedKeys as string[])
                }
                onSelect={handleTreeSelect}
                showLine={{ showLeafIcon: false }}
              />
            )}
          </Card>
        </Col>

        {/* Right: Rule Details */}
        <Col span={12}>
          <Card
            title={
              selectedTreeNode
                ? `Rule Details: ${
                    selectedTreeNode.rule?.name || "Transport Group"
                  }`
                : "Select a Rule"
            }
            size="small"
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
            bodyStyle={{ flex: 1, overflow: "auto" }}
          >
            {selectedTreeNode ? (
              <div>
                <Alert
                  message="Rule Information"
                  description="Select rules to view their details and manage components."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <div style={{ marginBottom: 16 }}>
                  <Text strong>Level: </Text>
                  <Text>{selectedTreeNode.level}</Text>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text strong>Total Cost: </Text>
                  <Text style={{ fontSize: "16px", fontWeight: "bold" }}>
                    €{selectedTreeNode.totalCost.toFixed(2)}
                  </Text>
                </div>

                {selectedTreeNode.rule && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong>Rule Details:</Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Transport: </Text>
                      <Text>{selectedTreeNode.rule.transport_type_name}</Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Components: </Text>
                      <Text>{selectedTreeNode.rule.component_count}</Text>
                    </div>
                    <Button
                      type="primary"
                      onClick={() => {
                        setSelectedRule(selectedTreeNode.rule!);
                        setIsComponentsDrawerVisible(true);
                      }}
                      style={{ marginTop: 16 }}
                    >
                      Manage Components
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 0",
                  color: "#999",
                }}
              >
                <Text>Select a rule from the tree to view details</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Modals and Drawers */}
      <AddRuleModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onSubmit={handleAddRule}
        referenceData={REFERENCE_DATA}
        checkForDuplicates={checkForDuplicates}
      />

      <ComponentsDrawer
        visible={isComponentsDrawerVisible}
        onClose={() => setIsComponentsDrawerVisible(false)}
        rule={selectedRule}
        components={components}
        onComponentsChange={() => {
          loadComponents();
          loadPricingRules();
        }}
      />
    </div>
  );
}

export default TreePricingTab2;
